import { type Ref, watch, type WatchStopHandle } from 'vue';
import { useRoute, useRouter } from 'vue-router';

interface SyncProperty<T> {
  key: string;
  ref: Ref<T>;
  type?: 'string' | 'number';
}

function convertType<T>(value: string, type?: string): T {
  if (type == undefined) {
    return value as unknown as T;
  }
  return (type == 'number' ? parseFloat(value) : value) as unknown as T;
}

export function useSyncWithQuery<T>(properties: SyncProperty<T>[]): {
  fillQueryParamValuesFromRefs: () => void;
  stopWatchers: () => void;
} {
  const route = useRoute();
  const router = useRouter();
  const query = ref({ ...route.query });
  const watchers = ref<WatchStopHandle[]>([]);

  const updateQueryParam = (key: string, value: T | T[]): void => {
    const newQuery: Record<string, any> = { ...query.value };
    if (value === null || value === undefined || value === '') {
      delete newQuery[key];
    } else {
      newQuery[key] = value;
    }

    query.value = newQuery;

    router.replace({ query: newQuery }).catch((err) => {
      if (err.name !== 'NavigationDuplicated') {
        console.error(err);
      }
    });
  };

  properties.forEach(({ key, ref, type }) => {
    const queryValue = route.query[key];
    /**
     * Read in the query value and set the ref to it
     * - this needs to happen in the server so the initial request for the page's data is correct
     */
    if (typeof queryValue === 'string') {
      ref.value = convertType<T>(queryValue, type);
    } else {
    }

    const unwatch = watch(ref, (newValue) => {
      updateQueryParam(key, newValue);
    });
    watchers.value.push(unwatch);
  });

  watch(
    () => route.query,
    (newQuery) => {
      properties.forEach(({ key, ref, type }) => {
        const queryValue = newQuery[key];
        if (typeof queryValue === 'string' && queryValue !== ref.value) {
          ref.value = convertType<T>(queryValue, type);
        } else if (queryValue === undefined && ref.value) {
          ref.value = null as unknown as T;
        }
      });
    },
    { deep: true }
  );

  const fillQueryParamValuesFromRefs = () => {
    properties.forEach(({ key, ref, type }) => {
      const queryValue = route.query[key];

      if (ref.value && route.query[key] === undefined) {
        updateQueryParam(key, ref.value);
      }

      const unwatch = watch(ref, (newValue) => {
        updateQueryParam(key, newValue);
      });
      watchers.value.push(unwatch);
    });
  };

  const stopWatchers = (): void => {
    watchers.value.forEach((unwatch) => unwatch());
  };

  return {
    stopWatchers,
    fillQueryParamValuesFromRefs,
  };
}
